/**
 * GlobalSearchBar – Always-visible search bar for the Navbar
 *
 * Renders a search input with typeahead dropdown, keyboard navigation,
 * and an AI assistant trigger button. Replaces the existing static
 * search input in the navbar.
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useSearch } from '../hooks/useSearch';
import { useSearchStore } from '../store/searchStore';
import { ENTITY_ICONS } from '../types';
import type { TypeaheadSuggestion } from '../types';
import { ThunderboltOutlined, HistoryOutlined } from '@ant-design/icons';
import '../styles/global-search.css';

const GlobalSearchBar: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const {
    query,
    setQuery,
    suggestions,
    suggestionsLoading,
    highlightedIndex,
    handleKeyDown,
    executeSearch,
    navigateToResult,
    clearSuggestions,
  } = useSearch();

  const { recentSearches, setAiDrawerOpen } = useSearchStore();

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        clearSuggestions();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [clearSuggestions]);

  // Ctrl+K now opens the Command Palette — removed conflicting shortcut

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      setDropdownOpen(true);
    },
    [setQuery],
  );

  const handleFocus = useCallback(() => {
    setDropdownOpen(true);
  }, []);

  const handleSuggestionClick = useCallback(
    (suggestion: TypeaheadSuggestion) => {
      setDropdownOpen(false);
      navigateToResult(suggestion);
    },
    [navigateToResult],
  );

  const handleRecentClick = useCallback(
    (recentQuery: string) => {
      setQuery(recentQuery);
      setDropdownOpen(false);
      executeSearch(recentQuery);
    },
    [setQuery, executeSearch],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setDropdownOpen(false);
      executeSearch();
    },
    [executeSearch],
  );

  const showDropdown =
    dropdownOpen && (suggestions.length > 0 || (query.length === 0 && recentSearches.length > 0));

  return (
    <div className="gs-search-bar" ref={containerRef}>
      <form onSubmit={handleSubmit} className="gs-search-bar__form">
        <svg
          className="gs-search-bar__icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="gs-search-bar__input"
          placeholder="Search customers, accounts, transactions… (⌘K)"
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          aria-label="Global search"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          role="combobox"
        />
        {suggestionsLoading && <span className="gs-search-bar__spinner" />}
        <button
          type="button"
          className="gs-search-bar__ai-btn"
          onClick={() => setAiDrawerOpen(true)}
          title="Open AI Search Assistant"
          aria-label="Open AI Search Assistant"
        >
          <ThunderboltOutlined />
        </button>
      </form>

      {showDropdown && (
        <div className="gs-dropdown" role="listbox">
          {suggestions.length > 0 && (
            <div className="gs-dropdown__section">
              <div className="gs-dropdown__section-title">Suggestions</div>
              {suggestions.map((s, idx) => (
                <div
                  key={s.id}
                  className={`gs-dropdown__item ${idx === highlightedIndex ? 'gs-dropdown__item--highlighted' : ''}`}
                  role="option"
                  aria-selected={idx === highlightedIndex}
                  onClick={() => handleSuggestionClick(s)}
                  onMouseEnter={() => useSearchStore.getState().setHighlightedIndex(idx)}
                >
                  <span className="gs-dropdown__item-icon">
                    {ENTITY_ICONS[s.entityType]}
                  </span>
                  <div className="gs-dropdown__item-text">
                    <span className="gs-dropdown__item-title">{s.title}</span>
                    <span className="gs-dropdown__item-subtitle">{s.subtitle}</span>
                  </div>
                  <span className="gs-dropdown__item-score">
                    {Math.round(s.score * 100)}%
                  </span>
                </div>
              ))}
            </div>
          )}

          {query.length === 0 && recentSearches.length > 0 && suggestions.length === 0 && (
            <div className="gs-dropdown__section">
              <div className="gs-dropdown__section-title">Recent Searches</div>
              {recentSearches.slice(0, 5).map((r) => (
                <div
                  key={r.timestamp}
                  className="gs-dropdown__item"
                  role="option"
                  aria-selected={false}
                  onClick={() => handleRecentClick(r.query)}
                >
                  <span className="gs-dropdown__item-icon"><HistoryOutlined /></span>
                  <div className="gs-dropdown__item-text">
                    <span className="gs-dropdown__item-title">{r.query}</span>
                    <span className="gs-dropdown__item-subtitle">
                      {r.resultCount} result{r.resultCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
